import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showNotification, captureException } from 'r/utils';
import { fetchApiRaw, POST_ANALYSES_VERIFY_CMS } from 'r/utils/fetchApiRaw';
import { StoredAuth } from 'r/utils/Stored';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { Analyse } from 'r/types';

interface ModalBulkVerificationProps {
  analyses: Analyse[];
  onSuccess: () => void;
}

export default function ModalBulkVerification({
  analyses,
  onSuccess,
}: ModalBulkVerificationProps) {
  const [isOpen, setIsOpen] = useState(true);
  const queryClient = useQueryClient();
  const sorted = useMemo(
    () =>
      [...analyses].sort((a, b) =>
        a.verifiedBy === b.verifiedBy ? 0
        : a.verifiedBy ? -1
        : 1,
      ),
    [analyses],
  );

  const [selected, setSelected] = useState<Analyse[]>(() =>
    analyses.filter((a) => !a.verifiedBy),
  );

  useEffect(() => {
    setSelected(analyses.filter((a) => !a.verifiedBy));
  }, [analyses]);

  const handleToggle = useCallback((analysis: Analyse) => {
    setSelected((prev) =>
      prev.some((a) => a.id === analysis.id) ?
        prev.filter((a) => a.id !== analysis.id)
      : [...prev, analysis],
    );
  }, []);

  const { mutateAsync: submitVerification, isPending: isSubmitting } =
    useMutation({
      mutationFn: async () => {
        const accessTokens = StoredAuth.getAll();
        await fetchApiRaw(
          POST_ANALYSES_VERIFY_CMS,
          { body: selected.map((a) => a.id) },
          accessTokens.cms,
        );
      },
      onSuccess: () => {
        showNotification('success', `Verified ${selected.length} analyses`);
        queryClient.invalidateQueries({ queryKey: ['cms-analyses'] });
        setIsOpen(false);
        onSuccess();
      },
      onError: (error) => {
        captureException(error);
        showNotification('error', 'Failed to verify analyses');
      },
    });

  const handleSubmit = async () => {
    try {
      await submitVerification();
    } catch {
      // error handled by mutation's onError, dialog stays open for retry
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onSuccess();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirm Bulk Verification</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="max-h-[50vh] overflow-y-auto space-y-3">
            {sorted.map((analysis) => {
              const isSelected = selected.some((a) => a.id === analysis.id);
              const isAlreadyVerified = !!analysis.verifiedBy;
              return (
                <div
                  key={analysis.id}
                  className={cn(
                    'flex items-start gap-3 p-3 border rounded-lg transition-colors',
                    isAlreadyVerified ?
                      'border-yellow-500 bg-yellow-500/10'
                    : 'border-(--textTertiary)',
                    isSelected && 'bg-card',
                  )}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={isAlreadyVerified}
                    onCheckedChange={() => handleToggle(analysis)}
                    className={cn(
                      'mt-1',
                      isAlreadyVerified && 'cursor-not-allowed opacity-50',
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-main">
                        {analysis.authorName}
                      </span>
                      {isAlreadyVerified && (
                        <span className="bg-yellow-500 text-main text-xs px-2 py-0.5 rounded">
                          Verified
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-main line-clamp-2">
                      {analysis.tweetText}
                    </p>
                    <p className="text-xs text-secondary mt-1">
                      {analysis.verifiedBy ?
                        `Verified by: ${analysis.verifiedBy}`
                      : 'Not verified'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={selected.length === 0 || isSubmitting}
            >
              {isSubmitting ? 'Verifying...' : `Confirm (${selected.length})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
